// TransactionsPage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Autocomplete,
  Divider,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  IconButton,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { useQueryClient } from '@tanstack/react-query';

// =============== NEW: React Query ===============
import { useQuery } from '@tanstack/react-query';

// =============== NEW: Import Dialog Components ===============
import EditTransactionDialog from './dialogs/EditTransactionDialog';
import DeleteTransactionDialog from './dialogs/DeleteTransactionDialog';
import PaymentDialog from './dialogs/PaymentDialog';
import ChargeDialog from './dialogs/ChargeDialog';
import CreditDialog from './dialogs/CreditDialog';
import ResponsiveDataView from '../components/ResponsiveDataView';
import ResponsiveToolbar from '../components/ResponsiveToolbar';
import MobileDialog from './dialogs/MobileDialog';

const theme = createTheme({
  direction: 'rtl',
  ...heIL,
  typography: {
    fontSize: 16,
  },
});

// ---- Toolbar for the DataGrid export/spinner ----
function CustomToolbar({ rows = [], columns = [], isExporting, setIsExporting, filters }) {
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      // Use local date components (not toISOString, which shifts to UTC and
      // sends the previous day for Israel users).
      const toLocalDate = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const params = {
        search: filters.searchTerm,
        transaction_type:
          filters.transactionType !== 'All' ? filters.transactionType : '',
        start_date: filters.startDate ? toLocalDate(filters.startDate) : '',
        end_date: filters.endDate ? toLocalDate(filters.endDate) : '',
        business_id: parseBusinessIdFromString(filters.searchTerm),
      };
      // remove any empty param
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== '')
      );

      const response = await API.get('/transactions/export-excel/', {
        responseType: 'blob',
        params: cleanParams,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'דוח_תנועות.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('שגיאה ביצוא קובץ - נסה שוב');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <Button
        variant="text"
        color="primary"
        size="small"
        onClick={handleExportExcel}
        disabled={isExporting}
      >
        {isExporting ? <CircularProgress size={24} /> : 'אקסל'}
      </Button>
    </GridToolbarContainer>
  );
}

// =============== Helper: 1 month back range ===============
function getOneMonthBackRange() {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  const start = new Date(now);
  start.setMonth(start.getMonth() - 1);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

// =============== Helper: parse business ID from string ===============
function parseBusinessIdFromString(str) {
  if (!str) return null;
  const match = /\((\d+)\)$/.exec(str.trim());
  return match ? match[1] : null;
}

// Transaction detail panel component
function TransactionDetailPanel({ transaction, transactionFields, transactionFieldsSchema, debtFieldsSchema, clientFieldsSchema, orderTransactionFieldsSchema, onEdit, onDelete, userRole }) {
  if (!transaction) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        אין מידע נוסף להצגה
      </Alert>
    );
  }

  // Helper function to get field label
  const getFieldLabel = (fieldName) => {
    return transactionFieldsSchema[fieldName]?.label || 
           debtFieldsSchema[fieldName]?.label || 
           clientFieldsSchema[fieldName]?.label ||
           orderTransactionFieldsSchema[fieldName]?.label ||
           fieldName;
  };

  // Helper function to format field value
  const formatFieldValue = (fieldName, value) => {
    if (value === null || value === undefined || value === '') {
      return '–';
    }

    // Special formatting for different field types
    if (fieldName === 'Type') {
      if (value === 'Payment') return 'תשלום';
      if (value === 'Charge') return 'חיוב';
      if (value === 'Credit') return 'זיכוי';
      return value;
    }

    if (fieldName === 'SubType') {
      // Use the debt field schema labels if available
      if (debtFieldsSchema[value]) {
        return debtFieldsSchema[value].label;
      }
      if (clientFieldsSchema[value]) {
        return clientFieldsSchema[value].label;
      }
      if (orderTransactionFieldsSchema[value]) {
        return orderTransactionFieldsSchema[value].label;
      }
      return value;
    }

    if (fieldName === 'Timestamp' || fieldName.includes('Date') || fieldName.includes('_at')) {
      const date = new Date(value);
      if (fieldName === 'Timestamp') {
        // Full date and time for timestamp
        return date.toLocaleString('he-IL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        // Just date for other date fields
        return date.toLocaleDateString('he-IL');
      }
    }

    if (fieldName === 'Amount' || fieldName === 'TotalAfterTR' || fieldName.includes('amount') || fieldName.includes('total') || fieldName === 'Price') {
      const amount = parseFloat(value) || 0;
      const formattedAmount = new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency: 'ILS',
      }).format(amount);
      
      // Special handling for Amount field to show direction
      if (fieldName === 'Amount' && transaction.Type) {
        const type = transaction.Type;
        if (type === 'Payment' || type === 'Credit') {
          return `${formattedAmount}-`;
        } else if (type === 'Charge') {
          return `${formattedAmount}+`;
        }
      }
      
      return formattedAmount;
    }

    if (fieldName === 'CheckNumber' || fieldName === 'check_number') {
      return `צ'ק מס' ${value}`;
    }

    if (fieldName === 'PaymentMethod' || fieldName === 'payment_method') {
      const methods = {
        'cash': 'מזומן',
        'credit_card': 'כרטיס אשראי',
        'bank_transfer': 'העברה בנקאית',
        'check': 'צ\'ק',
        'other': 'אחר'
      };
      return methods[value] || value;
    }

    if (typeof value === 'boolean') {
      return value ? 'כן' : 'לא';
    }

    return value.toString();
  };

  // Get transaction type color for text only
  const getTypeColor = (type) => {
    switch (type) {
      case 'Payment':
        return '#2e7d32';
      case 'Charge':
        return '#c62828';
      case 'Credit':
        return '#f57f17';
      default:
        return '#757575';
    }
  };

  // Helper function to format payment type
  const formatPaymentType = (type) => {
    const types = {
      'cash': 'מזומן',
      'credit_card': 'כרטיס אשראי',
      'bank_transfer': 'העברה בנקאית',
      'check': 'צ\'ק',
      'other': 'אחר'
    };
    return types[type] || type;
  };

  // Helper function to format check status
  const formatCheckStatus = (status) => {
    const statuses = {
      'pending': 'ממתין',
      'cleared': 'נפרע',
      'bounced': 'חזר',
      'cancelled': 'בוטל'
    };
    return statuses[status] || status;
  };

  // Helper function to get check status chip color
  const getCheckStatusColor = (status) => {
    switch (status) {
      case 'cleared': return 'success';
      case 'bounced': return 'error';
      case 'cancelled': return 'default';
      default: return 'warning';  // pending
    }
  };

  const typeColor = getTypeColor(transaction.Type);

  // Fields to exclude from display (internal fields and date fields shown separately)
  const excludeFields = ['TrID', 'Client', 'ClientName', 'ClientBusinessID', 'reason', 'payment_details', 'DiscountReason', 'DiscountNote', 'Comment', 'Comments', 'Timestamp', 'ActualDate'];
  
  // Text/note fields to display separately
  const noteFields = ['Note', 'Notes'];
  
  // Main fields to display (from transactionFields config, excluding internal ones)
  const mainFields = transactionFields.filter(field => 
    !excludeFields.includes(field) && 
    !noteFields.includes(field) &&
    transaction[field] !== undefined &&
    transaction[field] !== null &&
    transaction[field] !== ''
  );

  // Get all note fields from transaction fields that should be shown
  const configuredNoteFields = transactionFields.filter(field => 
    noteFields.includes(field)
  );

  // Check if user can edit (only for non-order related transactions and manager role)
  const canEdit = userRole === 'manager' &&
                  transaction.SubType !== 'OrderPayment' &&
                  transaction.SubType !== 'Order' &&
                  transaction.SubType !== 'OrderCancellation' &&
                  transaction.SubType !== 'PaymentReversal' &&
                  transaction.SubType !== 'PaymentAdjustment' &&
                  transaction.SubType !== 'OrderAdjustment';

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa' }}>
      {/* Transaction Type Header with Edit Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: typeColor }}>
            {formatFieldValue('Type', transaction.Type)}
            {transaction.SubType && (
              <Typography component="span" sx={{ fontWeight: 'normal', fontSize: '1.1rem', color: 'text.secondary', ml: 1 }}>
                {' - '}{formatFieldValue('SubType', transaction.SubType)}
              </Typography>
            )}
          </Typography>
          {transaction.IsEdited && (
            <Chip label="נערך" color="warning" size="small" />
          )}
        </Box>

        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="medium"
              onClick={() => onEdit(transaction)}
              sx={{
                fontWeight: 'bold',
                fontSize: '1rem',
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                }
              }}
            >
              ערוך תנועה
            </Button>
            <Button
              variant="outlined"
              size="medium"
              color="error"
              onClick={() => onDelete(transaction)}
              sx={{
                fontWeight: 'bold',
                fontSize: '1rem',
              }}
            >
              מחק תנועה
            </Button>
          </Box>
        )}
      </Box>

      {/* Main Information Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2, mb: 3 }}>
        {mainFields.map((fieldName) => (
          <Card key={fieldName} sx={{ boxShadow: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {getFieldLabel(fieldName)}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {formatFieldValue(fieldName, transaction[fieldName])}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Dates Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
        <Divider sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
          תאריכים
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
        <Card sx={{ boxShadow: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              תאריך בפועל
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              {transaction.ActualDate ? new Date(transaction.ActualDate).toLocaleDateString('he-IL') : '–'}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ boxShadow: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              תאריך רישום
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              {transaction.Timestamp ? new Date(transaction.Timestamp).toLocaleString('he-IL', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
              }) : '–'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Payment Methods Section - Only for Payment transactions */}
      {transaction.Type === 'Payment' && transaction.payment_methods?.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              אמצעי תשלום
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>

          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>סוג</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>סכום</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>פרטים</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaction.payment_methods.map((pm, index) => (
                  <TableRow key={pm.PaymentMethodID || index}>
                    <TableCell align="center">{formatPaymentType(pm.Type)}</TableCell>
                    <TableCell align="center">
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency', currency: 'ILS'
                      }).format(pm.Amount)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {pm.Type === 'check' && pm.check_details && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="body2">צ'ק #{pm.check_details.CheckNumber}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
                              {pm.check_details.BankName && (
                                <Typography variant="caption" color="text.secondary">
                                  בנק: {pm.check_details.BankName}
                                </Typography>
                              )}
                              {pm.check_details.AccountNumber && (
                                <Typography variant="caption" color="text.secondary">
                                  | חשבון: {pm.check_details.AccountNumber}
                                </Typography>
                              )}
                              {pm.check_details.DueDate && (
                                <Typography variant="caption" color="text.secondary">
                                  | לפירעון: {new Date(pm.check_details.DueDate).toLocaleDateString('he-IL')}
                                </Typography>
                              )}
                              <Chip
                                label={formatCheckStatus(pm.check_details.Status)}
                                size="small"
                                color={getCheckStatusColor(pm.check_details.Status)}
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          </Box>
                        )}
                        {pm.Type === 'credit_card' && (
                          <Typography variant="body2">
                            {pm.CardType && `${pm.CardType} `}
                            {pm.CardLastFour && `****${pm.CardLastFour}`}
                          </Typography>
                        )}
                        {pm.Type === 'bank_transfer' && (
                          <Typography variant="body2">
                            {pm.BankName}
                            {pm.BankAccountLast4 && ` ****${pm.BankAccountLast4}`}
                            {pm.TransferDate && ` (${new Date(pm.TransferDate).toLocaleDateString('he-IL')})`}
                          </Typography>
                        )}
                        {pm.Type === 'cash' && (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                        {pm.Type === 'other' && pm.Notes && (
                          <Typography variant="body2">{pm.Notes}</Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Notes Section - Always Show */}
      <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
        <Divider sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
          הערות ופרטים
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      {configuredNoteFields.length > 0 ? (
        configuredNoteFields.map((fieldName) => {
          const hasContent = transaction[fieldName] && transaction[fieldName].toString().trim() !== '';
          return (
            <Card key={fieldName} sx={{ mb: 2, boxShadow: 1 }}>
              <CardHeader 
                title={
                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                    {getFieldLabel(fieldName)}
                  </Typography>
                }
                sx={{ 
                  backgroundColor: '#f5f1b7ff', 
                  py: 1,
                }}
              />
              <CardContent>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '1rem',
                    lineHeight: 1.8,
                    color: hasContent ? '#424242' : 'text.secondary',
                    fontStyle: hasContent ? 'normal' : 'italic'
                  }}
                >
                  {hasContent ? transaction[fieldName] : 'אין תוכן'}
                </Typography>
              </CardContent>
            </Card>
          );
        })
      ) : (
        <Card sx={{ boxShadow: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              אין שדות הערות מוגדרים
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

const TransactionsPage = () => {
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const role = userProfile?.role;

  // User-specific sessionStorage keys for state persistence (prevents data leakage between users)
  const userId = userProfile?.username || 'unknown';
  const FILTERS_KEY = `transactions-page-filters-${userId}`;
  const PAGINATION_KEY = `transactions-page-pagination-${userId}`;

  // Get UI configuration from user profile
  const uiConfig = userProfile?.ui_config || {};
  const debtFieldsSchema = uiConfig.debt_fields_schema || {};
  const debtFields = uiConfig.debt_fields || [];  
  const clientFields = uiConfig.client_fields || [];
  const clientFieldsSchema = uiConfig.client_fields_schema || {};
  
  // Get transaction fields configuration
  const transactionFields = uiConfig.transaction_fields || [];
  const transactionFieldsSchema = uiConfig.transaction_fields_schema || {};
  const orderTransactionFieldsSchema = uiConfig.order_transaction_fields_schema || {}; 
  // Elements roles 
  const summaryAllowedRoles = uiConfig?.pages_configs?.TransactionsPage?.summary_roles || ['manager']; 
  const ActionsAllowedRoles = uiConfig?.pages_configs?.TransactionsPage?.action_buttons_roles || ['manager'];
  const ClientAllowedRoles = uiConfig.pages_configs?.ClientDetailPage?.allowed_roles || ["manager", "employee"];
 
  // Messages
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Transactions & Pagination
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [mobileTransactionDetail, setMobileTransactionDetail] = useState(null);

  // Initialize pagination from sessionStorage or use defaults
  const [paginationModel, setPaginationModel] = useState(() => {
    try {
      const saved = sessionStorage.getItem(PAGINATION_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load pagination from sessionStorage', e);
    }
    return { page: 0, pageSize: 25 };
  });

  const [rowCount, setRowCount] = useState(0);

  // Filter state
  const { start, end } = getOneMonthBackRange();

  // Initialize filters from sessionStorage or use defaults
  const [searchTerm, setSearchTerm] = useState(() => {
    try {
      const saved = sessionStorage.getItem(FILTERS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.searchTerm || '';
      }
    } catch (e) {
      console.error('Failed to load filters from sessionStorage', e);
    }
    return '';
  });

  const [selectedSearchValue, setSelectedSearchValue] = useState(() => {
    try {
      const saved = sessionStorage.getItem(FILTERS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.searchTerm || null;
      }
    } catch (e) {
      console.error('Failed to load filters from sessionStorage', e);
    }
    return null;
  });

  // Debounced autocomplete search state
  const [debouncedAutocompleteSearch, setDebouncedAutocompleteSearch] = useState('');
  const autocompleteTimerCleanup = React.useRef(null);

  const [transactionType, setTransactionType] = useState(() => {
    try {
      const saved = sessionStorage.getItem(FILTERS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.transactionType || 'All';
      }
    } catch (e) {
      console.error('Failed to load filters from sessionStorage', e);
    }
    return 'All';
  });

  // Dates are not cached - always use fresh defaults to avoid stale date ranges
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Order number filter (manual per-group extra field stored on Transaction.extra_fields.OrderNumber)
  const orderNumberEnabled = transactionFields.includes('OrderNumber');
  const [orderNumberFilter, setOrderNumberFilter] = useState(() => {
    try {
      const saved = sessionStorage.getItem(FILTERS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.orderNumberFilter || null;
      }
    } catch (e) {
      console.error('Failed to load filters from sessionStorage', e);
    }
    return null;
  });
  const [availableOrderNumbers, setAvailableOrderNumbers] = useState([]);
  // When an OrderNumber is selected, dates are cleared so the result isn't restricted
  // by an unrelated date window. Their prior values are stashed and restored on clear.
  const [savedDateRange, setSavedDateRange] = useState(null);

  const handleOrderNumberChange = (newValue) => {
    if (newValue && !orderNumberFilter) {
      setSavedDateRange({ startDate, endDate, selectedMonth });
      setStartDate(null);
      setEndDate(null);
      setSelectedMonth(null);
    } else if (!newValue && orderNumberFilter && savedDateRange) {
      setStartDate(savedDateRange.startDate);
      setEndDate(savedDateRange.endDate);
      setSelectedMonth(savedDateRange.selectedMonth);
      setSavedDateRange(null);
    }
    setOrderNumberFilter(newValue);
  };

  // Summary
  const [summaryData, setSummaryData] = useState({
    payment_total: 0,
    charge_total: 0,
    credit_total: 0,
    payment_breakdown: {
      cash: 0,
      check: 0,
      credit_card: 0,
      bank_transfer: 0,
      other: 0,
    },
  });
  const [showPaymentBreakdown, setShowPaymentBreakdown] = useState(false);

  // Dialog control states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [chargeDialogOpen, setChargeDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  // Delete transaction dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  // Excel exporting
  const [isExporting, setIsExporting] = useState(false);

  // Check search state
  const [checkSearchActive, setCheckSearchActive] = useState(false);
  const [checkSearchNumber, setCheckSearchNumber] = useState('');
  const [checkSearchAccount, setCheckSearchAccount] = useState('');
  const [checkSearchPopoverAnchor, setCheckSearchPopoverAnchor] = useState(null);
  const [savedFilters, setSavedFilters] = useState(null);
  const [checkSearchTrigger, setCheckSearchTrigger] = useState(0); // Trigger for re-fetching when search values change


  // ---------- 1) Debounced callback for autocomplete search ----------
  const debouncedSetAutocompleteSearch = React.useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedAutocompleteSearch(value);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // ---------- 2) Server-side autocomplete fetch function ----------
  async function fetchClientsAutocomplete(searchTerm = '') {
    const params = {
      light: true,
      limit: 100  // Always show 100 clients for consistency
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    const response = await API.get('/clients/', { params });
    return response.data;
  }

  // ---------- 3) Use React Query to load & cache the clients (server-side search) ----------
  const {
    data: clientsData = [],
    isLoading: isLoadingClients,
    isError: isErrorClients,
    error: errorClients,
  } = useQuery({
    queryKey: ['clients', 'autocomplete', debouncedAutocompleteSearch],
    queryFn: () => fetchClientsAutocomplete(debouncedAutocompleteSearch),
    staleTime: 5 * 60 * 1000, // 5 min
    gcTime: 10 * 60 * 1000, // 10 min (replaces deprecated cacheTime)
    refetchOnWindowFocus: true,
  });

  // Create autocomplete options with dummy "no results" record when empty (and user is searching)
  const autocompleteOptions = React.useMemo(() => {
    if (clientsData.length === 0 && debouncedAutocompleteSearch) {
      return ['אין תוצאות'];
    }
    return clientsData.map((c) => `${c.ClientName} (${c.ClientBusinessID})`);
  }, [clientsData, debouncedAutocompleteSearch]);

  // ---------- 2) Fetch transactions with filters ----------
  const buildParams = () => {
    // When check search is active, use only check search params + pagination
    if (checkSearchActive) {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        check_number: checkSearchNumber.trim(),
        check_account: checkSearchAccount.trim(),
      };
      return Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== ''));
    }

    // Normal filters
    const trimmedSearch = searchTerm?.trim() || '';
    const params = {
      page: paginationModel.page + 1, // DRF is 1-based
      page_size: paginationModel.pageSize,
      search: trimmedSearch,
      transaction_type: transactionType !== 'All' ? transactionType : '',
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : '',
      end_date: endDate ? format(endDate, 'yyyy-MM-dd') : '',
      business_id: trimmedSearch ? parseBusinessIdFromString(trimmedSearch) : '',
      order_number: orderNumberFilter || '',
    };
    // remove empty
    return Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== ''));
  };

  // ---------- Fetch available order numbers for the autocomplete (once on mount when enabled) ----------
  useEffect(() => {
    if (!orderNumberEnabled) return;
    let cancelled = false;
    API.get('/transactions/order-numbers/')
      .then((resp) => {
        if (!cancelled) {
          setAvailableOrderNumbers(resp.data?.order_numbers || []);
        }
      })
      .catch((err) => {
        console.error('שגיאה בטעינת מספרי הזמנה:', err);
      });
    return () => { cancelled = true; };
  }, [orderNumberEnabled]);

  const fetchTransactions = async () => {
    setLoadingTransactions(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const params = buildParams();
      const transactionsResp = await API.get('/transactions/', { params });
      setTransactions(transactionsResp.data.results);
      setRowCount(transactionsResp.data.count);

      // If manager, fetch summary
      if (summaryAllowedRoles.includes(role)) {
        const summaryResp = await API.get('/transactions/summary/', { params });
        setSummaryData(summaryResp.data);
      }
    } catch (err) {
      console.error('שגיאה בטעינת הדוח תנועות:', err);
      setErrorMsg('שגיאה בטעינת הדוח תנועות');
    } finally {
      setLoadingTransactions(false);
    }
  };

  // ---------- Check Search Handlers ----------
  const handleCheckSearchOpen = (event) => {
    setCheckSearchPopoverAnchor(event.currentTarget);
  };

  const handleCheckSearchClose = () => {
    setCheckSearchPopoverAnchor(null);
  };

  const handleCheckSearchExecute = () => {
    // Must have at least one search value
    if (!checkSearchNumber.trim() && !checkSearchAccount.trim()) {
      return;
    }

    // Only save filters if not already in check search mode
    if (!checkSearchActive) {
      setSavedFilters({
        searchTerm,
        selectedSearchValue,
        transactionType,
        startDate,
        endDate,
      });
    }

    handleCheckSearchClose();

    // Reset pagination to first page
    setPaginationModel(prev => ({
      ...prev,
      page: 0,
    }));

    if (checkSearchActive) {
      // Already active - increment trigger to force re-fetch with new values
      setCheckSearchTrigger(prev => prev + 1);
    } else {
      // Activate check search mode - this will trigger fetch via useEffect
      setCheckSearchActive(true);
    }
  };

  const handleCheckSearchClear = () => {
    // Restore saved filters
    if (savedFilters) {
      setSearchTerm(savedFilters.searchTerm);
      setSelectedSearchValue(savedFilters.selectedSearchValue);
      setTransactionType(savedFilters.transactionType);
      setStartDate(savedFilters.startDate);
      setEndDate(savedFilters.endDate);
      setSavedFilters(null);
    }

    // Clear check search state
    setCheckSearchActive(false);
    setCheckSearchNumber('');
    setCheckSearchAccount('');

    // Reset pagination to first page
    setPaginationModel(prev => ({
      ...prev,
      page: 0,
    }));
  };

  // Fetch when check search state changes or trigger increments (for re-search)
  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkSearchActive, checkSearchTrigger]);

  // Synchronize selectedSearchValue with searchTerm
  useEffect(() => {
    if (!searchTerm && selectedSearchValue) {
      // If searchTerm is empty but selectedSearchValue has a value, clear it
      setSelectedSearchValue(null);
    }
  }, [searchTerm, selectedSearchValue]);

  // Persist filters to sessionStorage (excluding dates to avoid stale date ranges)
  useEffect(() => {
    try {
      const filters = {
        searchTerm,
        transactionType,
        orderNumberFilter,
      };
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
    } catch (e) {
      console.error('Failed to save filters to sessionStorage', e);
    }
  }, [searchTerm, transactionType, orderNumberFilter, FILTERS_KEY]);

  // Persist pagination to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(PAGINATION_KEY, JSON.stringify(paginationModel));
    } catch (e) {
      console.error('Failed to save pagination to sessionStorage', e);
    }
  }, [paginationModel, PAGINATION_KEY]);

  // Reset page to 0 when filters change (prevent invalid page error)
  useEffect(() => {
    setPaginationModel(prev => ({
      ...prev,
      page: 0
    }));
  }, [searchTerm, transactionType, startDate, endDate, orderNumberFilter]);

  // Validate page number when transactions change
  useEffect(() => {
    // Skip validation while loading or if no data loaded yet
    if (loadingTransactions || transactions.length === 0) {
      return;
    }

    if (rowCount > 0) {
      const maxPage = Math.ceil(rowCount / paginationModel.pageSize) - 1;
      if (paginationModel.page > maxPage) {
        // Reset to last valid page
        setPaginationModel(prev => ({
          ...prev,
          page: Math.max(0, maxPage)
        }));
      }
    } else if (paginationModel.page !== 0) {
      // No data, reset to first page
      setPaginationModel(prev => ({
        ...prev,
        page: 0
      }));
    }
  }, [rowCount, paginationModel.pageSize, paginationModel.page, loadingTransactions, transactions.length]);

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const queryClient = useQueryClient();

  const invalidateClientListCache = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['clients'],
      refetchType: 'active', // Ensures active queries refetch immediately
    });
  };

  // ---------- Table columns ----------
  const columns = useMemo(() => {
    const columnsArray = [];
    
  // Always include ClientName column first for easier navigation
  columnsArray.push({
    field: 'ClientName',
    headerName: clientFieldsSchema?.ClientName?.label || 'שם הלקוח',
    flex: 0.5,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      const client = params.row?.Client || {};
      const canNavigateToClient = ClientAllowedRoles.includes(role);
      
      if (canNavigateToClient) {
        return (
          <Button
            variant="text"
            color="primary"
            onClick={() => navigate(`/client/${client.ClientBusinessID}`)}
            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
          >
            {client.ClientName}
          </Button>
        );
      } else {
        return (
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.primary',
              textAlign: 'center',
              fontSize: '1.1rem'
            }}
          >
            {client.ClientName}
          </Typography>
        );
      }
    },
  });
    
    // Get configured fields and ensure ActualDate is included (replacing Timestamp in the grid)
    const fieldsToShow = [...transactionFields];

    // Ensure ActualDate is in the list (it may not be in config yet)
    if (!fieldsToShow.includes('ActualDate')) {
      // Insert ActualDate where Timestamp would be, or at end
      const timestampIndex = fieldsToShow.indexOf('Timestamp');
      if (timestampIndex !== -1) {
        fieldsToShow.splice(timestampIndex, 0, 'ActualDate');
      } else {
        fieldsToShow.push('ActualDate');
      }
    }

    // Generate columns dynamically from transaction fields configuration
    for (const fieldName of fieldsToShow) {
      // Skip ClientName as we already added it first
      // Skip Timestamp - we use ActualDate for the datagrid (Timestamp shown in detail panel only)
      if (fieldName === 'ClientName' || fieldName === 'TrID' || fieldName === 'Timestamp') {
        continue;
      }

      const fieldConfig = transactionFieldsSchema[fieldName] || {};
      // Use Hebrew labels for specific fields
      let label = fieldConfig.label || fieldName;
      if (fieldName === 'ActualDate') {
        label = 'תאריך בפועל';
      } else if (fieldName === 'Timestamp') {
        label = 'תאריך רישום';
      } else if (fieldName === 'IsEdited') {
        label = 'נערך';
      }

      let column = {
        field: fieldName,
        headerName: label,
        flex: 0.5,
        minWidth: 120,
        align: 'center',
        headerAlign: 'center',
      };
      
      // Apply appropriate renderers based on field type
      if (fieldName === 'ClientBusinessID') {
        column.renderCell = (params) => {
          const client = params.row?.Client || {};
          return client.ClientBusinessID;
        };
        // column.hide = true;
      } 
      else if (fieldName === 'ActualDate') {
        // ActualDate shows just the date (no time)
        column.minWidth = 120;
        column.renderCell = (params) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleDateString('he-IL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
        };
      }
      else if (fieldName === 'Timestamp' || fieldConfig.type === 'date') {
        // Timestamp and other date fields show date and time
        column.minWidth = 170;
        column.renderCell = (params) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleString('he-IL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        };
      }
      else if (fieldName === 'IsEdited') {
        column.minWidth = 80;
        column.renderCell = (params) => {
          if (!params.value) return '';
          return (
            <Chip
              label="נערך"
              color="warning"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          );
        };
      }
      else if (fieldName === 'Type') {
        column.renderCell = (params) => {
          const t = params.value;
          if (t === 'Payment') return 'תשלום';
          if (t === 'Charge') return 'חיוב';
          if (t === 'Credit') return 'זיכוי';
          return t || '';
        };
      }
      else if (fieldName === 'SubType') {
      column.renderCell = (params) => {
        let subtype = params.value || '';
        // Use the debt field schema labels if available
        if (debtFieldsSchema[subtype]) {
          subtype = debtFieldsSchema[subtype].label;
        }
        if (clientFieldsSchema[subtype]) {
          subtype = clientFieldsSchema[subtype].label;
        }
        if (orderTransactionFieldsSchema[subtype]) {
          subtype = orderTransactionFieldsSchema[subtype].label
        }
        return (
          <Tooltip 
            title={subtype} 
            arrow 
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  color: 'black',
                  fontSize: '1.2rem',
                  maxWidth: 500,
                  padding: '10px 12px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap'
                }
              },
              arrow: {
                sx: {
                  color: 'white',
                  '&::before': {
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }
                }
              }
            }}
          >
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              {subtype}
            </div>
          </Tooltip>
        );
      };
      } 
      else if (fieldName === 'Amount' || fieldName === 'TotalAfterTR' || fieldConfig.type === 'number') {
        // Common handler for numeric fields
        column.valueGetter = (value, row) => {
          const v = row[fieldName];
          if (!v) return 0;
          return parseFloat(v) || 0;
        };
        
        column.renderCell = (params) => {
          const value = parseFloat(params.value || 0);
          const formattedValue = new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS',
          }).format(value);
          
          // Special display for Amount based on transaction type
          if (fieldName === 'Amount') {
            const type = params.row?.Type || '';
            switch (type) {
              case 'Payment':
              case 'Credit':
                return `${formattedValue}-`;
              case 'Charge':
                return `${formattedValue}+`;
            }
          }
          
          return formattedValue;
        };
      } 
      else if (fieldConfig.type === 'string' || fieldName === 'Invoice' || fieldName === 'Note') {
        // String fields with tooltip
        column.renderCell = (params) => {
          const value = params.value || '';
          return (
            <Tooltip 
              title={value} 
              arrow 
              placement="top"
              enterDelay={500}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    color: 'black',
                    fontSize: '1.2rem',
                    maxWidth: 500,
                    padding: '10px 12px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap'
                  }
                },
                arrow: {
                  sx: {
                    color: 'white',
                    '&::before': {
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }
                  }
                }
              }}
            >
              <div style={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                width: '100%',
                cursor: 'pointer',
                textAlign: 'center'
              }}>
                {value}
              </div>
            </Tooltip>
          );
        };
      }
      
      // Handle extra fields dynamically (fields not in standard transaction model)
      const standardFields = ['ClientName', 'ClientBusinessID', 'Type', 'SubType', 'Invoice', 'Amount', 'TotalAfterTR', 'Timestamp', 'ActualDate', 'Note', 'IsEdited'];
      if (!standardFields.includes(fieldName)) {
        column.valueGetter = (value, row) => {
          return row?.[fieldName] || '';
        };
      }
      
      columnsArray.push(column);
    }
    
    return columnsArray;
  }, [transactionFields, transactionFieldsSchema, debtFieldsSchema, clientFieldsSchema, navigate]);

  // ---------- Card Renderer for Mobile ----------
  const transactionCardRenderer = (row) => {
    const amount = parseFloat(row?.Amount || '0');
    const formatted = new Intl.NumberFormat('he-IL', {
      style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(Math.abs(amount));

    const typeLabels = { Payment: 'תשלום', Charge: 'חיוב', Credit: 'זיכוי' };
    const typeColors = { Payment: 'success.main', Charge: 'error.main', Credit: 'info.main' };
    const type = row?.Type || '';

    const dateStr = row?.ActualDate
      ? new Date(row.ActualDate).toLocaleDateString('he-IL')
      : '';

    // Transaction rows have a nested Client object, not top-level ClientName
    const client = row?.Client || {};
    const clientName = client.ClientName || '';
    const clientBizId = client.ClientBusinessID || '';

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {clientName || '—'}
            </Typography>
            {clientBizId && (
              <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {clientFieldsSchema?.ClientBusinessID?.label || 'תז/חפ'}: {clientBizId}
              </Typography>
            )}
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', color: typeColors[type] || 'text.primary', flexShrink: 0 }}
          >
            {formatted}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, color: 'text.secondary' }}>
          <Typography variant="body2">{typeLabels[type] || type}</Typography>
          {dateStr && <Typography variant="body2">· {dateStr}</Typography>}
        </Box>
      </Box>
    );
  };

  // ---------- Render ----------
  const currentFilters = {
    searchTerm,
    transactionType,
    startDate,
    endDate,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <ThemeProvider theme={theme}>
        <div dir="rtl">
          <Container maxWidth={false} sx={{ width: '100%', py: 2 }}>
            <Typography variant="h3" sx={{ mb: 3 }}>
              דוח תנועות
            </Typography>

            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2, fontSize: '1rem' }}>
                {errorMsg}
              </Alert>
            )}
            {successMsg && (
              <Alert severity="success" sx={{ mb: 2, fontSize: '1rem' }}>
                {successMsg}
              </Alert>
            )}

            {/* כרטיס פעולות */}
            {ActionsAllowedRoles.includes(role) && (
              <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 3 }}>
                <CardHeader
                  title={
                    <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                      פעולות
                    </Typography>
                  }
                  sx={{
                    backgroundColor: 'success.main',
                    color: 'white',
                    py: 1.5,
                  }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                      onClick={() => setPaymentDialogOpen(true)}
                    >
                      תשלום
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                      onClick={() => setChargeDialogOpen(true)}
                    >
                      חיוב
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                      onClick={() => setCreditDialogOpen(true)}
                    >
                      זיכוי
                    </Button>

                    {/* Divider */}
                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                    {/* Check Search Button */}
                    <Tooltip title="חיפוש צ'קים">
                      <Button
                        variant={checkSearchActive ? 'contained' : 'outlined'}
                        color={checkSearchActive ? 'secondary' : 'primary'}
                        size="medium"
                        onClick={handleCheckSearchOpen}
                        startIcon={<ReceiptLongIcon />}
                        sx={{
                          fontWeight: 'bold',
                          direction: 'ltr',
                          ...(checkSearchActive && {
                            backgroundColor: 'secondary.main',
                            '&:hover': {
                              backgroundColor: 'secondary.dark',
                            },
                          }),
                        }}
                      >
                        {checkSearchActive ? 'צ\'ק פעיל' : 'חפש צ\'ק'}
                      </Button>
                    </Tooltip>

                    {/* Clear Check Search Button - only visible when active */}
                    {checkSearchActive && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="medium"
                        onClick={handleCheckSearchClear}
                        startIcon={<CloseIcon />}
                        sx={{ fontWeight: 'bold', direction: 'ltr' }}
                      >
                        נקה
                      </Button>
                    )}
                  </Box>

                  {/* Check Search Active Indicator */}
                  {checkSearchActive && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">
                          מציג תנועות עם צ'קים התואמים:{' '}
                          {checkSearchNumber && <strong>מס' צ'ק: {checkSearchNumber}</strong>}
                          {checkSearchNumber && checkSearchAccount && ' | '}
                          {checkSearchAccount && <strong>חשבון: {checkSearchAccount}</strong>}
                        </Typography>
                      </Box>
                    </Alert>
                  )}

                  {/* Check Search Popover */}
                  <Popover
                    open={Boolean(checkSearchPopoverAnchor)}
                    anchorEl={checkSearchPopoverAnchor}
                    onClose={handleCheckSearchClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <Box sx={{ p: 2, width: 300 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        חיפוש צ'קים
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        label="מספר צ'ק"
                        value={checkSearchNumber}
                        onChange={(e) => setCheckSearchNumber(e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="הזן מספר צ'ק"
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="מספר חשבון"
                        value={checkSearchAccount}
                        onChange={(e) => setCheckSearchAccount(e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="הזן מספר חשבון"
                      />
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleCheckSearchClose}
                        >
                          ביטול
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleCheckSearchExecute}
                          disabled={!checkSearchNumber.trim() && !checkSearchAccount.trim()}
                          startIcon={<SearchIcon />}
                          sx={{direction: 'ltr'}}
                        >
                          חפש
                        </Button>
                      </Box>
                    </Box>
                  </Popover>
                </CardContent>
              </Card>
            )}

            {/* כרטיס סינון + טבלה */}
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardHeader
                title={
                  <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                    רשימת תנועות
                  </Typography>
                }
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  py: 1.5,
                }}
              />
              <CardContent>
                {/* סינון */}
                <ResponsiveToolbar>
                  <Autocomplete
                    freeSolo
                    options={autocompleteOptions}
                    loading={isLoadingClients}
                    loadingText="טוען..."
                    openOnFocus
                    filterOptions={(x) => x}  // Disable client-side filtering (we do server-side)
                    getOptionDisabled={(option) => option === 'אין תוצאות'}  // Make "no results" unselectable
                    value={selectedSearchValue}  // Only control selected value, not input text
                    disabled={checkSearchActive}
                    onInputChange={(event, newInputValue, reason) => {
                      // Clear previous timer
                      if (autocompleteTimerCleanup.current) {
                        autocompleteTimerCleanup.current();
                      }
                      // Debounce the API call - but DON'T update React state on every keystroke
                      autocompleteTimerCleanup.current = debouncedSetAutocompleteSearch(newInputValue);

                      // Clear searchTerm if input is emptied (manually deleted all text)
                      if (newInputValue.trim() === '' && searchTerm !== '') {
                        setSearchTerm('');
                        setSelectedSearchValue(null);
                      }
                    }}
                    onChange={(event, newValue) => {
                      // Ignore dummy "no results" record
                      if (newValue === 'אין תוצאות') {
                        return;
                      }

                      if (typeof newValue === 'string' && newValue.trim() !== '') {
                        // Selected or typed a non-empty value
                        setSearchTerm(newValue);
                        setSelectedSearchValue(newValue);
                      } else {
                        // Cleared or empty string
                        setSearchTerm('');
                        setSelectedSearchValue(null);
                        setDebouncedAutocompleteSearch('');
                        if (autocompleteTimerCleanup.current) {
                          autocompleteTimerCleanup.current();
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="חיפוש"
                        size="small"
                        placeholder="חפש לפי שם או תז/חפ"
                        sx={{ width: { xs: '100%', md: 300 }, minWidth: { md: 300 } }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <TextField
                    label="סוג תנועה"
                    select
                    size="small"
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value)}
                    disabled={checkSearchActive}
                    sx={{ minWidth: { md: 180 } }}
                  >
                    <MenuItem value="All">הכל</MenuItem>
                    <MenuItem value="Payment">תשלום</MenuItem>
                    <MenuItem value="Charge">חיוב</MenuItem>
                    <MenuItem value="Credit">זיכוי</MenuItem>
                  </TextField>

                  <DatePicker
                    label="חודש"
                    value={selectedMonth}
                    onChange={(newValue) => {
                      if (newValue) {
                        setSelectedMonth(newValue);
                        setStartDate(startOfMonth(newValue));
                        setEndDate(endOfMonth(newValue));
                      }
                    }}
                    views={['month', 'year']}
                    disabled={checkSearchActive || !!orderNumberFilter}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { direction: 'ltr', minWidth: { md: 150 } },
                      },
                    }}
                  />

                  <DatePicker
                    label="מתאריך"
                    value={startDate}
                    onChange={(newValue) => { setStartDate(newValue); setSelectedMonth(null); }}
                    format="dd/MM/yyyy"
                    disabled={checkSearchActive || !!orderNumberFilter}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { direction: 'ltr', minWidth: { md: 160 } },
                      },
                    }}
                  />

                  <DatePicker
                    label="עד תאריך"
                    value={endDate}
                    onChange={(newValue) => { setEndDate(newValue); setSelectedMonth(null); }}
                    format="dd/MM/yyyy"
                    disabled={checkSearchActive || !!orderNumberFilter}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { direction: 'ltr', minWidth: { md: 160 } },
                      },
                    }}
                  />

                  {orderNumberEnabled && (
                    <Autocomplete
                      size="small"
                      options={availableOrderNumbers}
                      value={orderNumberFilter}
                      onChange={(e, newValue) => handleOrderNumberChange(newValue)}
                      disabled={checkSearchActive}
                      sx={{ width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={transactionFieldsSchema.OrderNumber?.label || 'מספר הזמנה'}
                          size="small"
                        />
                      )}
                    />
                  )}

                  <Button
                    variant="contained"
                    size="large"
                    sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                    onClick={() => {
                      setPaginationModel({
                        pageSize: 25,
                        page: 0,
                      });
                      fetchTransactions();
                    }}
                    disabled={checkSearchActive}
                  >
                    סינון
                  </Button>
                </ResponsiveToolbar>

                <Divider sx={{ mb: 2 }} />

                {/* טבלת טרנזקציות */}
                {loadingTransactions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                    <Box sx={{
                      width: '100%',
                      '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        whiteSpace: 'normal !important',  // ADD THIS - allows header text to wrap
                        lineHeight: '1.2',  
                        overflow: 'visible', 
                      },
                      '.MuiDataGrid-cell': {
                        fontSize: '1.1rem',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      },
                    }}>
                    <ResponsiveDataView
                      rows={transactions}
                      density="comfortable"
                      columns={columns}
                      getRowId={(row) => row.TrID}
                      cardRenderer={transactionCardRenderer}
                      onRowClick={(row) => setMobileTransactionDetail(row)}
                      emptyMessage="אין תוצאות"
                      autoHeight
                      pagination
                      slots={{
                        toolbar: () => (
                          <CustomToolbar
                            rows={transactions}
                            columns={columns}
                            isExporting={isExporting}
                            setIsExporting={setIsExporting}
                            filters={currentFilters}
                          />
                        ),
                      }}
                      paginationMode="server"
                      rowCount={rowCount}
                      pageSizeOptions={[25, 50, 100]}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      initialState={{
                        sorting: {
                          sortModel: [{ field: 'ActualDate', sort: 'desc' }],
                        },
                      }}
                      disableSelectionOnClick
                      localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                      // Detail panel configuration
                      getDetailPanelContent={({ row }) => (
                        <TransactionDetailPanel
                          transaction={row}
                          transactionFields={transactionFields}
                          transactionFieldsSchema={transactionFieldsSchema}
                          debtFieldsSchema={debtFieldsSchema}
                          clientFieldsSchema={clientFieldsSchema}
                          orderTransactionFieldsSchema={orderTransactionFieldsSchema}
                          onEdit={(transaction) => {
                            setEditingTransaction(transaction);
                            setEditDialogOpen(true);
                          }}
                          onDelete={(transaction) => {
                            setDeletingTransaction(transaction);
                            setDeleteDialogOpen(true);
                          }}
                          userRole={role}
                        />
                      )}
                      getDetailPanelHeight={() => 'auto'}
                      components={{
                        NoRowsOverlay: () => (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '100%',
                            }}
                          >
                            אין תוצאות
                          </Box>
                        ),
                      }}
                      getRowClassName={(params) => {
                        const type = params.row?.Type;
                        switch (type) {
                          case 'Payment':
                            return 'payment-row';
                          case 'Charge':
                            return 'charge-row';
                          case 'Credit':
                            return 'credit-row';
                          default:
                            return '';
                        }
                      }}
                      sx={{
                        // ADD THESE CSS CLASSES:
                        '& .payment-row': {
                          backgroundColor: '#e8f5e9', // Light green
                          '&:hover': {
                            backgroundColor: '#c8e6c9', // Slightly darker green on hover
                          },
                        },
                        '& .charge-row': {
                          backgroundColor: '#ffebee', // Light red/pink
                          '&:hover': {
                            backgroundColor: '#ffcdd2', // Slightly darker red on hover
                          },
                        },
                        '& .credit-row': {
                          backgroundColor: '#fff8e1', // Light yellow
                          '&:hover': {
                            backgroundColor: '#ffecb3', // Slightly darker yellow on hover
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* כרטיס סיכום סוגי טרנזקציות (למנהל בלבד) */}
            {/* =============== Transaction Summary Line =============== */}
            {summaryAllowedRoles.includes(role) && (
              <Box sx={{ mt: 2 }}>
                {/* Summary Title */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'right' }}>
                  סיכום סינון נוכחי:
                </Typography>
                
                {/* Summary Items */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-start', // Right align in RTL layout
                  alignItems: 'center', 
                  gap: 3,
                  flexWrap: 'wrap' 
                }}>
                  {/* Payment Summary with Breakdown */}
                  <Box sx={{
                    backgroundColor: '#e8f5e9',
                    borderRadius: 2,
                    border: '1px solid #c8e6c9',
                    overflow: 'hidden',
                  }}>
                    <Box
                      sx={{
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: summaryData.payment_total > 0 ? 'pointer' : 'default',
                        '&:hover': summaryData.payment_total > 0 ? {
                          backgroundColor: 'rgba(0,0,0,0.02)',
                        } : {},
                      }}
                      onClick={() => summaryData.payment_total > 0 && setShowPaymentBreakdown(!showPaymentBreakdown)}
                    >
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                        סך תשלומים:{' '}
                        <Typography component="span" sx={{ fontWeight: 'bold' }}>
                          {new Intl.NumberFormat('he-IL', {
                            style: 'currency',
                            currency: 'ILS',
                          }).format(summaryData.payment_total)}
                        </Typography>
                      </Typography>
                      {summaryData.payment_total > 0 && (
                        <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                          {showPaymentBreakdown ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </Box>
                    <Collapse in={showPaymentBreakdown}>
                      <Box sx={{
                        px: 2,
                        pb: 1.5,
                        pt: 0.5,
                        borderTop: '1px dashed #c8e6c9',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}>
                        {summaryData.payment_breakdown?.cash > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PaymentsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              מזומן: {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(summaryData.payment_breakdown.cash)}
                            </Typography>
                          </Box>
                        )}
                        {summaryData.payment_breakdown?.check > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiptLongIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              צ'קים: {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(summaryData.payment_breakdown.check)}
                            </Typography>
                          </Box>
                        )}
                        {summaryData.payment_breakdown?.credit_card > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CreditCardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              כרטיס אשראי: {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(summaryData.payment_breakdown.credit_card)}
                            </Typography>
                          </Box>
                        )}
                        {summaryData.payment_breakdown?.bank_transfer > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccountBalanceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              העברה בנקאית: {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(summaryData.payment_breakdown.bank_transfer)}
                            </Typography>
                          </Box>
                        )}
                        {summaryData.payment_breakdown?.other > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MoreHorizIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              אחר: {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(summaryData.payment_breakdown.other)}
                            </Typography>
                          </Box>
                        )}
                        {/* Unregistered payment type - difference between total and categorized */}
                        {(() => {
                          const categorizedTotal = (summaryData.payment_breakdown?.cash || 0) +
                            (summaryData.payment_breakdown?.check || 0) +
                            (summaryData.payment_breakdown?.credit_card || 0) +
                            (summaryData.payment_breakdown?.bank_transfer || 0) +
                            (summaryData.payment_breakdown?.other || 0);
                          const unregistered = summaryData.payment_total - categorizedTotal;
                          if (unregistered > 0.01) {
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MoreHorizIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                   לא נרשם סוג: {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(unregistered)}
                                </Typography>
                              </Box>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Charge Summary */}
                  <Box sx={{ 
                    backgroundColor: '#ffebee', // Light red/pink (same as charge rows)
                    padding: '8px 16px',
                    borderRadius: 2,
                    border: '1px solid #ffcdd2',
                    display: 'inline-block'
                  }}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                      סך חיובים:{' '}
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(summaryData.charge_total)}
                      </Typography>
                    </Typography>
                  </Box>

                  {/* Credit Summary */}
                  <Box sx={{ 
                    backgroundColor: '#fff8e1', // Light yellow (same as credit rows)
                    padding: '8px 16px',
                    borderRadius: 2,
                    border: '1px solid #ffecb3',
                    display: 'inline-block'
                  }}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                      סך זיכויים:{' '}
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(summaryData.credit_total)}
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* ========== Payment Dialog ========== */}
            <PaymentDialog
              open={paymentDialogOpen}
              onClose={() => setPaymentDialogOpen(false)}
              debtFields={debtFields}
              debtFieldsSchema={debtFieldsSchema}
              fetchTransactions={fetchTransactions}
              invalidateClientListCache={invalidateClientListCache}
              userProfile={userProfile}
            />

            {/* ========== Charge Dialog ========== */}
            <ChargeDialog
              open={chargeDialogOpen}
              onClose={() => setChargeDialogOpen(false)}
              debtFields={debtFields}
              debtFieldsSchema={debtFieldsSchema}
              fetchTransactions={fetchTransactions}
              invalidateClientListCache={invalidateClientListCache}
              userProfile={userProfile}
            />

            {/* ========== Credit Dialog ========== */}
            <CreditDialog
              open={creditDialogOpen}
              onClose={() => setCreditDialogOpen(false)}
              debtFields={debtFields}
              debtFieldsSchema={debtFieldsSchema}
              fetchTransactions={fetchTransactions}
              invalidateClientListCache={invalidateClientListCache}
              userProfile={userProfile}
            />
            {/* ========== Mobile Transaction Detail Dialog ========== */}
            <MobileDialog
              open={Boolean(mobileTransactionDetail)}
              onClose={() => setMobileTransactionDetail(null)}
              maxWidth="md"
              fullWidth
            >
              {mobileTransactionDetail && (
                <>
                  <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      פרטי תנועה
                    </Typography>
                    <IconButton onClick={() => setMobileTransactionDetail(null)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ p: 0 }}>
                    <TransactionDetailPanel
                      transaction={mobileTransactionDetail}
                      transactionFields={transactionFields}
                      transactionFieldsSchema={transactionFieldsSchema}
                      debtFieldsSchema={debtFieldsSchema}
                      clientFieldsSchema={clientFieldsSchema}
                      orderTransactionFieldsSchema={orderTransactionFieldsSchema}
                      onEdit={(transaction) => {
                        setMobileTransactionDetail(null);
                        setEditingTransaction(transaction);
                        setEditDialogOpen(true);
                      }}
                      onDelete={(transaction) => {
                        setMobileTransactionDetail(null);
                        setDeletingTransaction(transaction);
                        setDeleteDialogOpen(true);
                      }}
                      userRole={role}
                    />
                  </DialogContent>
                </>
              )}
            </MobileDialog>

            {/* ========== Edit Transaction Dialog ========== */}
            <EditTransactionDialog
              open={editDialogOpen}
              onClose={() => {
                setEditDialogOpen(false);
                setEditingTransaction(null);
              }}
              transaction={editingTransaction}
              transactionFields={transactionFields}
              transactionFieldsSchema={transactionFieldsSchema}
              debtFieldsSchema={debtFieldsSchema}
              clientFieldsSchema={clientFieldsSchema}
              orderTransactionFieldsSchema={orderTransactionFieldsSchema}
              fetchTransactions={fetchTransactions}
              userProfile={userProfile}
            />

            {/* ========== Delete Transaction Dialog ========== */}
            <DeleteTransactionDialog
              open={deleteDialogOpen}
              onClose={() => {
                setDeleteDialogOpen(false);
                setDeletingTransaction(null);
              }}
              transaction={deletingTransaction}
              onSuccess={fetchTransactions}
            />
          </Container>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default TransactionsPage;